import {
  assertSafeInteger,
  type SimulationSeed,
  type SimulationStream,
  simulationSeed,
} from '@kenocyber/shared';

/**
 * Deterministic **simulation** randomness.
 *
 * This generator drives the visible hunt: mob spawns, movement, spawn order,
 * the boss timer, starfields and effects. Its seed is handed to the browser so
 * the client can render exactly the run the server will later revalidate.
 *
 * ## What this is NOT for
 *
 * Gacha, rare drops, purchasable rewards and anything convertible to Cash.
 * SplitMix64 is fully predictable from a seed the player can read, and its
 * entire state is one 64-bit integer, so an economic outcome derived from it
 * could be predicted and farmed by restarting a session. Those outcomes use the
 * server-only economic randomness contracts in
 * `@kenocyber/shared` (`randomness/economic`), never this module.
 *
 * The type system enforces the split: this module only accepts
 * {@link SimulationSeed} and {@link SimulationStream}, and no economic label
 * exists in that allow-list.
 *
 * @see docs/SECURITY.md
 */

const MASK_64 = (1n << 64n) - 1n;
const GOLDEN_GAMMA = 0x9e3779b97f4a7c15n;
const MIX_A = 0xbf58476d1ce4e5b9n;
const MIX_B = 0x94d049bb133111ebn;

/** FNV-1a 64-bit, used to fold a stream label into the seed. */
const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;

function mix64(value: bigint): bigint {
  let z = value & MASK_64;
  z = ((z ^ (z >> 30n)) * MIX_A) & MASK_64;
  z = ((z ^ (z >> 27n)) * MIX_B) & MASK_64;
  return (z ^ (z >> 31n)) & MASK_64;
}

function hashLabel(label: string): bigint {
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < label.length; i += 1) {
    hash = ((hash ^ BigInt(label.charCodeAt(i))) * FNV_PRIME) & MASK_64;
  }
  return hash;
}

/**
 * Validates a stream index without ever coercing it.
 *
 * The index participates in seed derivation, so a silently truncated `1.9` or a
 * `NaN` would produce a seed nobody can reproduce during revalidation.
 */
function normalizeStreamIndex(index: bigint | number): bigint {
  if (typeof index === 'bigint') {
    if (index < 0n) {
      throw new RangeError(`simulation stream index must not be negative, received ${index}`);
    }
    return index & MASK_64;
  }
  assertSafeInteger(index, 'simulation stream index');
  if (index < 0) {
    throw new RangeError(`simulation stream index must not be negative, received ${index}`);
  }
  return BigInt(index);
}

/**
 * Derives an independent, reproducible simulation sub-seed.
 *
 * `deriveSimulationSeed(runSeed, 'boss-timer', 3)` always yields the same value,
 * so the server can replay the client's run step by step.
 *
 * `stream` is restricted to {@link SimulationStream}: an economic purpose
 * cannot be derived from a client-visible seed.
 */
export function deriveSimulationSeed(
  rootSeed: SimulationSeed,
  stream: SimulationStream,
  index: bigint | number,
): SimulationSeed {
  const indexBig = normalizeStreamIndex(index);
  return simulationSeed(mix64(mix64((rootSeed & MASK_64) ^ hashLabel(stream)) ^ indexBig));
}

/**
 * The complete state of a generator, as plain serialisable data.
 *
 * `bigint` values are decimal STRINGS: JSON has no bigint, and a snapshot that
 * round-trips through the database must come back bit-identical or a replay
 * would diverge.
 */
export interface SimulationRngState {
  readonly seed: string;
  readonly state: string;
  readonly drawCount: number;
}

export interface SimulationRng {
  /** The seed this generator was created from. Log it with every run. */
  readonly seed: SimulationSeed;
  /** How many values have been drawn so far. Part of the replay record. */
  readonly drawCount: number;
  /** Next raw 64-bit value. */
  nextUint64(): bigint;
  /** Uniform in `[0, 1)`, built from 53 bits so it is exactly representable. */
  nextUnitInterval(): number;
  /** Uniform integer in `[min, max]`, rejection-sampled so it is unbiased. */
  nextIntInclusive(min: number, max: number): number;
  /**
   * Everything needed to resume this exact stream later.
   *
   * This is what makes a hunt resumable across a page reload and, more
   * importantly, what lets the SERVER continue a run from the state it last
   * validated instead of trusting a client to describe it.
   */
  snapshot(): SimulationRngState;
}

/**
 * SplitMix64.
 *
 * Chosen because the whole state is a single 64-bit integer: it serialises to
 * one database column, it is trivial to reimplement in another language when
 * revalidating a run, and it has no hidden warm-up behaviour. Those same
 * properties are exactly why it must not be used for economic outcomes.
 */
export function createSimulationRng(
  seed: SimulationSeed,
  /**
   * Resumes a stream mid-flight. Only ever built from {@link SimulationRng.snapshot};
   * passing hand-written values would produce a run nobody can reproduce.
   */
  resume?: { readonly state: bigint; readonly drawCount: number },
): SimulationRng {
  const initialSeed = simulationSeed(seed & MASK_64);
  let state: bigint = resume ? resume.state & MASK_64 : initialSeed;
  let draws = resume ? resume.drawCount : 0;

  const nextUint64 = (): bigint => {
    state = (state + GOLDEN_GAMMA) & MASK_64;
    draws += 1;
    return mix64(state);
  };

  return {
    seed: initialSeed,
    get drawCount() {
      return draws;
    },
    nextUint64,
    nextUnitInterval(): number {
      // Top 53 bits: the exact mantissa width of a double.
      return Number(nextUint64() >> 11n) / 2 ** 53;
    },
    nextIntInclusive(min: number, max: number): number {
      assertSafeInteger(min, 'nextIntInclusive min');
      assertSafeInteger(max, 'nextIntInclusive max');
      if (max < min) {
        throw new RangeError(`invalid range [${min}, ${max}]`);
      }
      if (!Number.isSafeInteger(max - min)) {
        throw new RangeError(`range [${min}, ${max}] is wider than Number can represent exactly`);
      }
      const span = BigInt(max - min) + 1n;
      // Rejection sampling: plain modulo would bias the low end of the range.
      const limit = MASK_64 - (MASK_64 % span);
      let candidate = nextUint64();
      while (candidate >= limit) {
        candidate = nextUint64();
      }
      return min + Number(candidate % span);
    },
    snapshot(): SimulationRngState {
      return { seed: initialSeed.toString(10), state: state.toString(10), drawCount: draws };
    },
  };
}

/**
 * Rebuilds a generator that continues exactly where the snapshot left off.
 *
 * Rejects anything that could not have come from {@link SimulationRng.snapshot}:
 * a malformed state would not fail loudly, it would silently produce a
 * different run, which is the one failure mode a replay-validated economy
 * cannot tolerate.
 */
export function restoreSimulationRng(state: SimulationRngState): SimulationRng {
  const seed = parseUint64Decimal(state.seed, 'rng seed');
  const current = parseUint64Decimal(state.state, 'rng state');
  assertSafeInteger(state.drawCount, 'rng drawCount');
  if (state.drawCount < 0) {
    throw new RangeError(`rng drawCount must not be negative, received ${state.drawCount}`);
  }
  return createSimulationRng(simulationSeed(seed), { state: current, drawCount: state.drawCount });
}

function parseUint64Decimal(value: unknown, label: string): bigint {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return failParse(label);
  }
  const parsed = BigInt(value);
  if (parsed > MASK_64) {
    return failParse(label);
  }
  return parsed;
}

function failParse(label: string): never {
  // No value echo: the caller's input is untrusted data, not a message.
  throw new RangeError(`${label} is not a valid unsigned 64-bit decimal string`);
}
